import { useState, useEffect } from 'react';

import { FormField } from '../../components/FormField';
import { FormSection } from '../../components/FormSection';
import { HeightInput } from '../../components/HeightInput';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile, useUpdateProfile } from '../../hooks/useProfile';
import {
  convertFeetInchesToCm,
  convertCmToFeetInches,
} from '../../utils/heightConversion';

/**
 * Profile Page
 * Access: All roles
 *
 * User profile management:
 * - View personal information
 * - Update demographic details
 * - Manage preferences
 */

export function ProfilePage() {
  const { user } = useAuth();
  const { data: profile, isLoading, error } = useProfile();
  const updateProfile = useUpdateProfile();

  const [isEditing, setIsEditing] = useState(false);
  const [email, setEmail] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [heightCm, setHeightCm] = useState('');
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const [sexAssignedAtBirth, setSexAssignedAtBirth] = useState<
    'male' | 'female' | 'prefer-not-to-say'
  >('prefer-not-to-say');
  const [measurementSystem, setMeasurementSystem] = useState<
    'metric' | 'imperial'
  >('metric');

  // Load profile data when it becomes available
  useEffect(() => {
    if (profile) {
      setEmail(profile.email || user?.email || '');
      setPreferredName(profile.preferredName || '');
      setFamilyName(profile.familyName || '');
      setDateOfBirth(profile.dateOfBirth || '');
      setSexAssignedAtBirth(profile.sexAssignedAtBirth || 'prefer-not-to-say');
      setMeasurementSystem(profile.measurementSystem || 'metric');

      if (profile.heightCm) {
        setHeightCm(profile.heightCm.toString());
        // Convert to feet/inches
        const { feet, inches } = convertCmToFeetInches(profile.heightCm);
        setHeightFeet(feet.toString());
        setHeightInches(inches.toString());
      }
    }
  }, [profile, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let heightInCm: number;
      if (measurementSystem === 'metric') {
        heightInCm = parseFloat(heightCm);
      } else {
        const feet = parseInt(heightFeet);
        const inches = parseInt(heightInches);
        heightInCm = convertFeetInchesToCm(feet, inches);
      }

      await updateProfile.mutateAsync({
        email,
        preferredName,
        familyName,
        dateOfBirth,
        heightCm: heightInCm,
        sexAssignedAtBirth,
        measurementSystem,
      });

      setIsEditing(false);
    } catch (error) {
      // Error display is handled by checking updateProfile.isError below
      // Keep form in edit mode so user can retry
      console.error('Profile update failed:', error);
    }
  };

  const handleCancel = () => {
    // Reset to profile data
    if (profile) {
      setEmail(profile.email || user?.email || '');
      setPreferredName(profile.preferredName || '');
      setFamilyName(profile.familyName || '');
      setDateOfBirth(profile.dateOfBirth || '');
      setSexAssignedAtBirth(profile.sexAssignedAtBirth || 'prefer-not-to-say');
      setMeasurementSystem(profile.measurementSystem || 'metric');

      if (profile.heightCm) {
        setHeightCm(profile.heightCm.toString());
        const { feet, inches } = convertCmToFeetInches(profile.heightCm);
        setHeightFeet(feet.toString());
        setHeightInches(inches.toString());
      }
    }
    setIsEditing(false);
  };

  if (error) {
    return (
      <div className="p-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-red-900 mb-2">
              Failed to Load Profile
            </h2>
            <p className="text-red-700 mb-4">
              {error instanceof Error
                ? error.message
                : 'An error occurred while loading your profile'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="max-w-3xl mx-auto">
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-text-dark">My Profile</h1>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
            >
              Edit Profile
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Personal Information */}
          <FormSection title="Personal Information">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                id="preferredName"
                label="Preferred Name"
                value={preferredName}
                onChange={(e) => setPreferredName(e.target.value)}
                disabled={!isEditing}
              />

              <FormField
                id="familyName"
                label="Family Name"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                disabled={!isEditing}
              />
            </div>

            <FormField
              label="Date of Birth"
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              disabled={!isEditing}
            />
          </FormSection>

          {/* Demographics */}
          <FormSection title="Demographics">
            <HeightInput
              value={{
                heightCm,
                heightFeet,
                heightInches,
              }}
              onChange={(value) => {
                if (value.heightCm !== undefined) setHeightCm(value.heightCm);
                if (value.heightFeet !== undefined)
                  setHeightFeet(value.heightFeet);
                if (value.heightInches !== undefined)
                  setHeightInches(value.heightInches);
              }}
              measurementSystem={measurementSystem}
              disabled={!isEditing}
            />

            <FormField
              label="Sex Assigned at Birth"
              type="select"
              value={sexAssignedAtBirth}
              onChange={(e) =>
                setSexAssignedAtBirth(
                  e.target.value as 'male' | 'female' | 'prefer-not-to-say'
                )
              }
              description="Used for metabolic rate and health calculations"
              disabled={!isEditing}
              options={[
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
                { value: 'prefer-not-to-say', label: 'Prefer not to say' },
              ]}
            />
          </FormSection>

          {/* Preferences */}
          <FormSection title="Preferences">
            <div>
              <label className="block text-sm font-medium mb-2 text-text-dark">
                Measurement System
              </label>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="measurementSystem"
                    value="metric"
                    checked={measurementSystem === 'metric'}
                    onChange={(e) => {
                      setMeasurementSystem(e.target.value as 'metric');
                      // Convert imperial to metric if height values exist
                      const feet = parseInt(heightFeet) || 0;
                      const inches = parseInt(heightInches) || 0;
                      if (feet > 0 || inches > 0) {
                        const cm = convertFeetInchesToCm(feet, inches);
                        setHeightCm(cm.toString());
                      }
                    }}
                    disabled={!isEditing}
                    className="mr-2"
                  />
                  Metric (cm, kg)
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="measurementSystem"
                    value="imperial"
                    checked={measurementSystem === 'imperial'}
                    onChange={(e) => {
                      setMeasurementSystem(e.target.value as 'imperial');
                      // Convert metric to imperial if height value exists
                      if (heightCm) {
                        const cm = parseFloat(heightCm);
                        if (!isNaN(cm) && cm > 0) {
                          const { feet, inches } = convertCmToFeetInches(cm);
                          setHeightFeet(feet.toString());
                          setHeightInches(inches.toString());
                        }
                      }
                    }}
                    disabled={!isEditing}
                    className="mr-2"
                  />
                  Imperial (ft/in, lb)
                </label>
              </div>
            </div>
          </FormSection>

          {/* Account Information */}
          <FormSection title="Account Information">
            <FormField
              id="email"
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!isEditing}
            />

            <div>
              <label
                htmlFor="userId"
                className="block text-xs font-medium mb-2 text-text-secondary"
              >
                User ID (for support reference)
              </label>
              <input
                id="userId"
                type="text"
                value={user?.username || ''}
                disabled
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm bg-beige text-text-secondary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-text-dark">
                Roles
              </label>
              <div className="flex gap-2">
                {user?.roles.map((role) => (
                  <span
                    key={role}
                    className="px-3 py-1 rounded-full text-sm font-medium bg-primary text-white"
                  >
                    {role}
                  </span>
                ))}
              </div>
            </div>
          </FormSection>

          {/* Action Buttons */}
          {isEditing && (
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateProfile.isPending}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
              >
                {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}

          {/* Error Message */}
          {updateProfile.isError && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-700">
                Failed to update profile. Please try again.
                {updateProfile.error instanceof Error && (
                  <span className="block text-sm mt-1">
                    {updateProfile.error.message}
                  </span>
                )}
              </p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
